import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import "./PreviewIFrame.scss";

import { setLoading, setMenuState, setErrorModal, setInitializeState } from "_core/slices/scene/SceneSlice";
import AdminGetWebPageApi from "apis/AdminGetWebPageApi";
import { Stack } from "_core/components/common/UI";
import { InfoCard } from "components/common/parts";
import * as Constants from "_core/configs/Constants";

export const Comment = `
/**
 *
 * props の定義
 *
 * [set]
 *      key         ... key監視対象
 *      config      ... サイトに反映する設定情報
 *      hostName    ... プレビュー表示を行うWebサイトのホスト名
 *      tag         ... Webサイトに埋め込むscriptタグのテキスト
 *
 * 例)
 *      <PreviewIFrame
 *          config={}
 *          hostName="https://evolve.ne.jp"
 *          tag="<script src='prizmo.js' defer data-conf-url=sample.json></script>"
 *      />
 *
 */
`

export const PreviewIFrame = (props) => {
    const dispatch = useDispatch();
    const ExecAdminGetWebPageApi = AdminGetWebPageApi();
    const isRequest = useRef(false);
    const isInsert = useRef(false);
    // ページ取得APIの応答待ちフラグ
    // isRequest は応答が返ってから立つため、待っている間は別経路からの再入を防げない
    const isFetching = useRef(false);
    const [isRendered, setIsRendered] = useState(false);

    const { tUser } = useSelector((store) => store.transaction);

    let iframeRef = useRef(null);
    let srcDoc = useRef("");

    const prizmoLoaded = useRef(false);
    const adminLoaded = useRef(false);

    // コンポーネント読み込み時の処理
    useEffect(() => {
        // postMessage の受信ハンドラ
        const handleMessage = (event) => {
            // メッセージデータの形式を確認
            if (event.data && event.data.type === "linkClick") {
                console.log("受信したメッセージ:", event.data);
            }

            if (props.onURLChanged)
            {
                props.onURLChanged(event.data.url)
            }
        };

        // メッセージイベントのリスナーを設定
        window.addEventListener("message", handleMessage);

        // クリーンアップ
        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, []);

    const tryInitialize = (iframe) => {
        if (prizmoLoaded.current && adminLoaded.current) {
            // 管理画面ホストにAPIを向ける
            iframe.contentWindow.loadWidget();
            iframe.contentWindow.setDomain(props.targetDomain);
            iframe.contentWindow.setDebug();
            iframe.contentWindow.setEndpoint(Constants.BASE_URL);
            iframe.contentWindow.loadConfigObject(props.config);
        }
    };


    // ページが切り替わったときの読み込み処理
    const handleIframeLoaded = async () => {
        if (props.isLoading)
        {
            return
        }

        console.log("Iframe更新チェック");
        console.log(props.hostName);
        if (! props.hostName || props.hostName === "" || ! props.hostName.includes("https://"))
        {
            return
        }

        // ページ読み込み実行処理
        const AdminGetWebPageRequest = async (url) => {
            // Webページ取得API
    
            // 通信中の表示を出す
            dispatch(setLoading(true));
    
            const requestParams = {
                url: url
            };
    
            const result = await ExecAdminGetWebPageApi(requestParams);
    
            // 通信中の表示を消す
            dispatch(setLoading(false));
            if (result.error || (result.response && result.response.error_code > 0))
            {
                // エラーモーダル表示
                dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
            }
            else
            {
                return result.response.page;
            }
        };

        // iframeを取得する
        const iframe = document.getElementById("inner-frame");

        // リクエスト済みフラグが立っている場合はスキップ
        if (isRequest.current) {
            if (isInsert.current) {
                return;
            }
            if (props.config) {
                const body = iframe.contentWindow.document.body;

                // scriptタグを作成して追加
                const scriptNode = document.createElement('script');
                scriptNode.src = tUser.config.prizmo_js_path ? `${Constants.PRIZMO_JS_UNIQUE_PATH}${tUser.config.prizmo_js_path}` : Constants.PRIZMO_JS_URL[tUser.config.version];
                scriptNode.defer = true;
                scriptNode.setAttribute("data-conf-url", props.jsonPath);
                body.appendChild(scriptNode);

                // 管理画面用scriptタグを作成して追加
                const adminScriptNode = document.createElement('script');
                adminScriptNode.src = Constants.REACT_APP_BASE_PATH + "/prizmo_admin.js";
                adminScriptNode.defer = true;
                body.appendChild(adminScriptNode);
    
                scriptNode.addEventListener("load", () => {
                    prizmoLoaded.current = true;
                    tryInitialize(iframe);
                });

                adminScriptNode.addEventListener("load", () => {
                    adminLoaded.current = true;
                    tryInitialize(iframe);
                });
                
                // 読み込めないサイトへの対応処理は下記jsの中で実行する
                const scriptNodePreprocess = document.createElement('script');
                scriptNodePreprocess.src = Constants.REACT_APP_BASE_PATH + "/preprocess.js";
                scriptNodePreprocess.defer = true;
                body.appendChild(scriptNodePreprocess);

                scriptNodePreprocess.addEventListener("load", () => {
                    iframe.contentWindow.preprocess(props.hostName);
                });
                setIsRendered(true)
            }

            if (iframe && iframe.contentWindow && iframe.contentDocument) {
                console.log("クリックイベント発行");
                const links = iframe.contentDocument.querySelectorAll("a");
            
                links.forEach((link) => {
                    // イベントリスナーにReactのメソッドを渡す
                    link.addEventListener("click", (event) => {
                        event.preventDefault(); // デフォルトの動作を防ぐ
                        const url = link.href;
                        console.log(url);

                        iframe.contentWindow.parent.postMessage(
                            { type: 'linkClick', url: url },
                            Constants.FRONT_URL
                        );
                    });
                });
            }

            isInsert.current = true;
            return;
        }

        // 応答待ちの間に別経路(iframeのonLoad / hostNameのuseEffect)から再入すると、
        // 同じURLを二重に取りに行ってしまうためここで弾く
        if (isFetching.current) {
            return;
        }
        isFetching.current = true;

        // ページ取得APIをリクエストする
        await AdminGetWebPageRequest(props.hostName)
        .then((response) => {
            if (!response) return;

            // prizmo設置済みページの場合、置き換える
            let responseText = response.replace(
                /<script\s+src=['"](\/?\.\/javascript\/prizmo\.js|https:\/\/prizmosa\.blob\.core\.windows\.net\/prizmosa-public\/prizmo\.js)['"][^>]*><\/script>[\r\n]*/g,
                ''
            );

            // 相対パスでリソースを取得している箇所を絶対パスに置き換える
            responseText = responseText.replaceAll(
                /(src|href|srcset)=["'](?!https?:\/\/|\/\/|#)([^"']+)["']/g,
                (match, attr, path) => {
                    if (attr !== "srcset") {
                        // srcやhrefの場合の処理
                        return `${attr}="${resolveFullURL(props.hostName, path)}"`;
                    } else {    
                        // srcsetの場合の処理
                        const updatedSrcset = path
                            .split(",") // カンマで分割
                            .map(entry => {
                                const [url, descriptor] = entry.trim().split(/\s+/); // URLと解像度を分離
                                const resolvedURL = resolveFullURL(props.hostName, url); // URLを解決
                                return descriptor ? `${resolvedURL} ${descriptor}` : resolvedURL; // 解像度がある場合は付与
                            })
                            .join(", "); // 再度カンマ区切りで連結

                        return `${attr}="${updatedSrcset}"`;
                    }
                }
            );

            responseText = responseText.replaceAll(
                /(src|href|srcset)=["']([^"']+)["']/g,
                (match, attr, path) => {
                    if (attr !== "srcset") {
                        
                        if (path.includes(".js") || path.includes(".css"))
                        {
                            return `${attr}="${Constants.BASE_URL}/admin/get_web_page_from_param?url=${encodeURIComponent(path)}"`;
                        }

                        return `${attr}="${path}"`;
                    } else {    
                        return `${attr}="${path}"`;
                    }
                }
            );

            if (props.onFetched)
            {
                props.onFetched(responseText)
            }

            srcDoc.current = responseText;
            // iframeに反映する
            iframe.srcdoc = srcDoc.current;
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            isFetching.current = false;
        });

        // リクエスト済みフラグを上げる
        isRequest.current = true;
        setIsRendered(true)
    };

    // URLに変更があった場合
    useEffect(() => {
        if (props.isLoading)
        {
            return
        }

        // 明示的にクリーンアップする関数
        const cleanupIframe = () => {
            console.log("iframeのクリーンアップを実行");
            const iframe = iframeRef.current;
            if (iframe) {
                iframe.src = "about:blank"; // 現在のリソースを解放
                iframe.contentWindow?.location.replace("about:blank"); // さらに安全にリセット
                iframe.srcdoc = "<div></div>";
            }
        };

        if (isRequest.current) {
            console.log("再読み込み実行");
            cleanupIframe();
            isRequest.current = false;
            isInsert.current = false;
            setIsRendered(false);
            prizmoLoaded.current = false;
            adminLoaded.current = false;
        }
        else {
            handleIframeLoaded();
        }
    }, [props.hostName]);

    // htmlに影響があるパラメータに変更があった場合
    useEffect(() => {
        // iframeの初期化が終わっていない場合はスキップ
        if (!isInsert.current) {
            return;
        }

        if (!iframeRef.current.contentWindow.updateHero){
            return
        }

        // iframe内のページ情報を再初期化
        iframeRef.current.contentWindow.updateHero(props.config);
        setIsRendered(true)

    }, [props.config.topTargetId]);

    // テキストに影響があるパラメータに変更があった場合
    useEffect(() => {
        // iframeの初期化が終わっていない場合はスキップ
        if (!isInsert.current) {
            return;
        }

        if (!iframeRef.current.contentWindow.updateText){
            return
        }

        // iframe内のページ情報を再初期化
        iframeRef.current.contentWindow.updateText(props.config);
        setIsRendered(true)

    }, [props.config.chatWelcomText, props.config.buttonText, props.config.topTargetId, props.config.topWelcomeText]);

    // 動画に影響があるパラメータに変更があった場合
    useEffect(() => {
        // iframeの初期化が終わっていない場合はスキップ
        if (!isInsert.current) {
            return;
        }

        if (!iframeRef.current.contentWindow.updateMovie){
            return
        }

        // iframe内のページ情報を再初期化
        iframeRef.current.contentWindow.updateMovie(props.config);
        setIsRendered(true)

    }, [props.config.prMovieUrl, props.config.prTalkMovieUrl]);

    // 声に影響があるパラメータに変更があった場合
    useEffect(() => {
        // iframeの初期化が終わっていない場合はスキップ
        if (!isInsert.current) {
            return;
        }

        if (!iframeRef.current.contentWindow.updateVoice){
            return
        }

        // iframe内のウィジェットが使う声を差し替える
        iframeRef.current.contentWindow.updateVoice(props.config);
    }, [props.config.voiceId]);

    // スタイル変更のみ行う場合
    useEffect(() => {
        // iframeの初期化が終わっていない場合はスキップ
        if (!isInsert.current) {
            return;
        }

        if (!iframeRef.current.contentWindow.updateWidjet){
            return
        }

        // iframe内のページ情報を再初期化
        iframeRef.current.contentWindow.updateWidjet(props.config);
        setIsRendered(true)
    }, [props.config.position, props.config.buttonCol, props.config.buttonHoverCol, props.config.buttonTextCol, props.config.theme]);

    // 会話画面の開き方の変更を行う場合
    useEffect(() => {
        // iframeの初期化が終わっていない場合はスキップ
        if (!isInsert.current) {
            return;
        }

        if (!iframeRef.current.contentWindow.updateConfig){
            return
        }

        // iframe内のデータを更新
        iframeRef.current.contentWindow.updateConfig(props.config);
        setIsRendered(true)

    }, [props.config.openMode, props.config.destPage.url, props.config.typingEffect]);

    // 相対パスを完全なURLに変換する関数
    const resolveFullURL = (baseURL, path) => {
        // URLコンストラクタで絶対URLを生成
        return new URL(path, baseURL).href;
    }

    // コンポーネントの返却
    return (
        <div className={"preview-iframe-wrapper " + (props.className ? props.className : "")  + (! isRendered ? " isloading" : "")}>
            {(!props.isLoading) &&
                <iframe
                    id="inner-frame"
                    className={"preview-iframe "}
                    srcDoc="<div></div>"
                    title="Preview"
                    onLoad={handleIframeLoaded}
                    ref={iframeRef}
                />
            }
            {(! isRendered || props.isLoading) &&
                <Stack className="editor-preview-loading width-full height-full flex-center align-center">
                    <div className="loading-spinner">
                        <div className="loader"></div>
                    </div>
                </Stack>
            }
            {(!props.isLoading && props.hostName == "") &&
                <Stack className="editor-preview-loading width-full height-full flex-center align-center">
                    <InfoCard className="width-240px">プレビューするURLを入力してください</InfoCard>
                </Stack>
            }
        </div>
    );
};

export default PreviewIFrame;